import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { TableCell as TableCellComponent } from 'smartsuite-ui';
import DashboardContext from '../../../../shared/hooks/dashboardContext';
import tableCellRenderTypes from './tableCellRenderTypes';
import FlightDetailLink from '../../../../customizations/smartBag/tableCells/FlightDetailLink';
import BaggageDetailLink from '../../../../customizations/smartBag/tableCells/BaggageDetailLink';
import PassengerDetailLinkWithPassengerId from '../../../../customizations/smartBag/tableCells/PassengerDetailLinkWithPassengerId';
import StatusIcon from './StatusIcon';
import DateTime from './DateTime';
import Duration from './Duration';
import ThresholdCompare from './ThresholdCompare';
import DashboardLink from './DashboardLink';
import { getFormattedValue } from '../../../dashboardUtils';
import AdvancedSearchLink from './AdvancedSearchLink';
import StackedBar from './StackedBar';
import ColorCell from './ColorCell';
import TracerRecordDetailLink from '../../../../customizations/smartBag/tableCells/TracerRecordDetailLink';
import BagCargoCountLink from '../../../../customizations/smartBag/tableCells/BagCargoCountLink';

export default function TableCell(props) {
    const { customizations } = useContext(DashboardContext);

    const supplementalTableCell = customizations?.supplementalTableCellRenderer
        ? customizations?.supplementalTableCellRenderer(props)
        : undefined;

    if (supplementalTableCell) {
        return supplementalTableCell;
    }

    const {
        columnConfig,
        dataColorMappings,
        formatValue,
        metricRequest,
        openLinksInNewTab,
        resultRow,
        thresholds,
    } = props;

    const cellValue = resultRow[columnConfig.dataKey];
    const alertLevelNone = 0;

    switch (columnConfig.renderType) {
        case tableCellRenderTypes.colorCell: {
            return (
                <ColorCell
                    columnConfig={columnConfig}
                    metricRequest={metricRequest}
                    openLinksInNewTab={openLinksInNewTab}
                    resultRow={resultRow}
                    value={getFormattedValue(
                        cellValue,
                        columnConfig.suffix,
                        formatValue
                    )}
                    isLeftAligned={columnConfig.isLeftAligned}
                />
            );
        }

        case tableCellRenderTypes.dashboardLink: {
            return (
                <DashboardLink
                    columnConfig={columnConfig}
                    metricRequest={metricRequest}
                    dataRow={resultRow}
                    formatValue={formatValue}
                    openLinksInNewTab={openLinksInNewTab}
                    value={cellValue}
                />
            );
        }

        case tableCellRenderTypes.dateTime: {
            return (
                <DateTime
                    cellValue={cellValue}
                    dateTimeFormat={columnConfig.dateTimeFormat}
                    isLeftAligned={columnConfig.isLeftAligned}
                />
            );
        }

        case tableCellRenderTypes.duration: {
            return (
                <Duration
                    cellValue={cellValue}
                    durationTimeFormat={columnConfig.timeFormat}
                    isLeftAligned={columnConfig.isLeftAligned}
                />
            );
        }

        case tableCellRenderTypes.stackedBar: {
            return (
                <StackedBar
                    comparisonMetricKey={columnConfig.comparisonMetricKey}
                    dataColorMappings={dataColorMappings}
                    resultRow={resultRow}
                    legendFormatter={props.legendFormatter}
                    metricKeys={columnConfig.metricKeys}
                />
            );
        }

        case tableCellRenderTypes.statusIcon: {
            return <StatusIcon cellValue={cellValue} />;
        }

        case tableCellRenderTypes.thresholdCompare: {
            return (
                <ThresholdCompare
                    colorWhenBad={columnConfig.colorWhenBad}
                    colorWhenGood={columnConfig.colorWhenGood}
                    expectValueOverThreshold={
                        columnConfig.expectValueOverThreshold
                    }
                    formatValue={formatValue}
                    isLeftAligned={columnConfig.isLeftAligned}
                    suffix={columnConfig.suffix}
                    threshold={thresholds.find(
                        (x) => x.id === columnConfig.thresholdId
                    )}
                    value={resultRow[columnConfig.valueDataKey]}
                />
            );
        }

        case tableCellRenderTypes.text:
            return (
                <TableCellComponent
                    cssClassModifiers={[
                        columnConfig.isLeftAligned ? 'left-align' : '',
                    ]}
                >
                    {getFormattedValue(
                        cellValue,
                        columnConfig.suffix,
                        formatValue
                    )}
                </TableCellComponent>
            );

        case tableCellRenderTypes.alternateText: {
            return (
                <TableCellComponent
                    cssClassModifiers={[
                        columnConfig.isLeftAligned ? 'left-align' : '',
                    ]}
                >
                    {getFormattedValue(
                        resultRow[columnConfig.valueDataKey],
                        columnConfig.suffix,
                        formatValue
                    )}
                </TableCellComponent>
            );
        }

        case tableCellRenderTypes.advancedSearchLink: {
            return (
                <AdvancedSearchLink
                    columnConfig={columnConfig}
                    metricRequest={metricRequest}
                    openLinksInNewTab={openLinksInNewTab}
                    resultRow={resultRow}
                    value={formatValue(resultRow[columnConfig.dataKey])}
                />
            );
        }

        case tableCellRenderTypes.inboundToUnloadBagCargoCountStatusLink: {
            return (
                <BagCargoCountLink
                    alertLevel={resultRow[columnConfig.stateDataKey]}
                    columnConfig={columnConfig}
                    resultRow={resultRow}
                    exists={resultRow[columnConfig.inboundExists]}
                />
            );
        }

        case tableCellRenderTypes.inboundUnloadedBagCargoCountLink: {
            return (
                <BagCargoCountLink
                    alertLevel={alertLevelNone}
                    columnConfig={columnConfig}
                    resultRow={resultRow}
                    exists={resultRow[columnConfig.inboundExists]}
                />
            );
        }

        case tableCellRenderTypes.outboundToLoadBagCargoCountStatusLink: {
            return (
                <BagCargoCountLink
                    alertLevel={resultRow[columnConfig.stateDataKey]}
                    columnConfig={columnConfig}
                    resultRow={resultRow}
                    exists={resultRow[columnConfig.outboundExists]}
                />
            );
        }

        case tableCellRenderTypes.outboundLoadedBagCargoCountLink: {
            return (
                <BagCargoCountLink
                    alertLevel={alertLevelNone}
                    columnConfig={columnConfig}
                    resultRow={resultRow}
                    exists={resultRow[columnConfig.outboundExists]}
                />
            );
        }

        case tableCellRenderTypes.flightDetailLink: {
            return (
                <TableCellComponent>
                    <FlightDetailLink
                        columnConfig={columnConfig}
                        resultRow={resultRow}
                        openInNewTab={true}
                    >
                        {resultRow[columnConfig.dataKey]}
                    </FlightDetailLink>
                </TableCellComponent>
            );
        }

        case tableCellRenderTypes.baggageDetailLink: {
            return (
                <TableCellComponent>
                    <BaggageDetailLink
                        columnConfig={columnConfig}
                        resultRow={resultRow}
                        openInNewTab={true}
                    >
                        {resultRow[columnConfig.dataKey]}
                    </BaggageDetailLink>
                </TableCellComponent>
            );
        }

        case tableCellRenderTypes.passengerDetailLinkWithPassengerId: {
            return (
                <TableCellComponent>
                    <PassengerDetailLinkWithPassengerId
                        columnConfig={columnConfig}
                        resultRow={resultRow}
                        openInNewTab={true}
                    >
                        {resultRow[columnConfig.dataKey]}
                    </PassengerDetailLinkWithPassengerId>
                </TableCellComponent>
            );
        }

        case tableCellRenderTypes.checkmark: {
            return (
                <TableCellComponent>{cellValue ? '✓' : ''}</TableCellComponent>
            );
        }

        case tableCellRenderTypes.tracerReferenceLink: {
            return (
                <TableCellComponent>
                    <TracerRecordDetailLink
                        columnConfig={columnConfig}
                        resultRow={resultRow}
                        openInNewTab={true}
                    >
                        {resultRow[columnConfig.idDataKey]}
                    </TracerRecordDetailLink>
                </TableCellComponent>
            );
        }

        case tableCellRenderTypes.percent: {
            return <TableCell>{`${cellValue}%`}</TableCell>;
        }

        default: {
            return (
                <TableCellComponent
                    cssClassModifiers={[
                        columnConfig.isLeftAligned ? 'left-align' : '',
                    ]}
                >
                    {getFormattedValue(
                        cellValue,
                        columnConfig.suffix,
                        formatValue
                    )}
                </TableCellComponent>
            );
        }
    }
}

TableCell.propTypes = {
    columnConfig: PropTypes.instanceOf(Object).isRequired,
    dataColorMappings: PropTypes.instanceOf(Object),
    formatValue: PropTypes.func.isRequired,
    legendFormatter: PropTypes.func.isRequired,
    metricRequest: PropTypes.instanceOf(Object).isRequired,
    openLinksInNewTab: PropTypes.bool.isRequired,
    resultRow: PropTypes.instanceOf(Object).isRequired,
    thresholds: PropTypes.instanceOf(Array).isRequired,
};
